import { evidenceSidebarOpenAtom } from '@/atoms/evidenceSidebarAtoms';
import { useSetAtom } from 'jotai';
import { useRouter } from 'next/router';
import { useEffect, useRef } from 'react';

// Loose prefix matches `/scribe/session`, `/scribe/sessions`, and any
// child path of either — deliberately mirroring `useCurrentSessionId`
// (`src/hooks/useCurrentIds.ts`) so this hook can't disagree with the
// canonical session-detection. The over-match risk (e.g. a future
// `/scribe/session-archive`) is the *safe* direction here: it would
// leave the panel open during the transition (reverting to the pre-fix
// behaviour for that one route), whereas under-matching a real session
// route would close the panel mid-session and trigger
// `useClearPatientSummaryDeliveryOnSidebarClose`.
const isSessionPath = (path: string) => path.startsWith('/scribe/session');

/**
 * Strip a leading locale segment using the i18n locales declared on the
 * router (e.g. /en/scribe/chat → /scribe/chat). Reading from
 * `router.locales` instead of a hard-coded regex keeps the hook resilient
 * when locales are added to next.config.js — including the `en-psaccent`
 * pseudolocale used in dev/QA.
 */
const stripLocalePrefix = (
  path: string,
  locales: readonly string[] | undefined
): string => {
  if (!locales?.length) return path;
  const firstSegment = path.split('/', 2)[1];
  if (!firstSegment) return path;
  return locales.includes(firstSegment) ? path.slice(firstSegment.length + 1) : path;
};

/**
 * Close the right-side Evidence sidebar atom when the user navigates away
 * from a session route. EVI-1599: without this, the panel stays mounted
 * with the previous session's chat content during the brief Next.js page
 * transition, overlapping the destination page's loading state.
 *
 * Closing on `routeChangeStart` lets the panel's existing 500ms collapse
 * animation play during the route load instead of the chat UI hanging at
 * full width until the next page finishes mounting.
 *
 * Hook is mounted inside `EvidenceSidebarPanel`, which renders on
 * `/scribe/session/<id>` (singular, via `ResizableLayout`'s
 * `DualPanelLayout`) and `/scribe/sessions[/<id>]` (plural, via the
 * sessions list page itself) — i.e. anywhere a session might be
 * active. So any routeChangeStart that targets a non-session URL means
 * the user is leaving the session surface.
 *
 * Trade-off: if a navigation aborts after `routeChangeStart` fires (rare —
 * mostly hits when a second navigation interrupts the first), the panel
 * will remain collapsed even though the user stays on the session route.
 * The user can reopen it manually; we accept this over the much more
 * visible bug of the panel hanging at full width during every load.
 */
export const useCloseEvidenceSidebarOnLeaveSession = () => {
  const router = useRouter();
  const setEvidenceSidebarOpen = useSetAtom(evidenceSidebarOpenAtom);

  // Read latest asPath/locales via refs so the listener can subscribe
  // once on mount instead of tearing down + re-attaching on every
  // navigation (asPath changes per route change).
  const asPathRef = useRef(router.asPath);
  const localesRef = useRef(router.locales);
  asPathRef.current = router.asPath;
  localesRef.current = router.locales;

  useEffect(() => {
    const handleRouteChangeStart = (url: string) => {
      const stripped = stripLocalePrefix(url, localesRef.current);

      // routeChangeStart also fires for shallow updates and
      // `<Link href={asPath}>` clicks. `router.asPath` is the current
      // (locale-stripped) URL during routeChangeStart, so a match means
      // the navigation isn't actually leaving the page — bail out before
      // collapsing the panel.
      if (stripped === asPathRef.current) return;

      if (!isSessionPath(stripped)) {
        setEvidenceSidebarOpen(false);
      }
    };

    router.events.on('routeChangeStart', handleRouteChangeStart);
    return () => {
      router.events.off('routeChangeStart', handleRouteChangeStart);
    };
  }, [router.events, setEvidenceSidebarOpen]);
};
